import type { FilterFn } from '@tanstack/table-core';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import {
  DataTableFilterOperatorTypes,
  DateFilterOperatorType,
  EmptyFilterValue,
  FilterOperatorTypes,
  FilterType,
  FilterTypes,
  FilterValue,
  FilterValueType,
  MultiSelectFilterOperatorType,
  NumberFilterOperatorType,
  SelectFilterOperatorType,
  StringFilterOperatorType
} from '~/types/filters';
import { DataTableFilterValues } from '../data-table.types';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type FilterPrimitive = string | string[] | number | boolean | Date;

export type FilterFunctionsMap = {
  number: Record<NumberFilterOperatorType, FilterFn<unknown>>;
  string: Record<StringFilterOperatorType, FilterFn<unknown>>;
  date: Record<DateFilterOperatorType, FilterFn<unknown>>;
  select: Record<SelectFilterOperatorType, FilterFn<unknown>>;
  multiselect: Record<MultiSelectFilterOperatorType, FilterFn<unknown>>;
};

export const filterOperationsMap: FilterFunctionsMap = {
  number: {
    eq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) === Number(filterValue.value);
    },
    neq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) !== Number(filterValue.value);
    },
    lt: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) < Number(filterValue.value);
    },
    lte: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) <= Number(filterValue.value);
    },
    gt: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) > Number(filterValue.value);
    },
    gte: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return Number(row.getValue(columnId)) >= Number(filterValue.value);
    }
  },
  string: {
    eq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return (
        String(row.getValue(columnId)).toLowerCase() ===
        String(filterValue.value).toLowerCase()
      );
    },
    neq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return (
        String(row.getValue(columnId)).toLowerCase() !==
        String(filterValue.value).toLowerCase()
      );
    },
    contains: (row, columnId, filterValue: FilterValue, _addMeta) => {
      const columnValue = String(row.getValue(columnId)).toLowerCase();
      const filterStr = String(filterValue.value).toLowerCase();
      return columnValue.includes(filterStr);
    },
    starts_with: (row, columnId, filterValue: FilterValue, _addMeta) => {
      const columnValue = String(row.getValue(columnId)).toLowerCase();
      const filterStr = String(filterValue.value).toLowerCase();
      return columnValue.startsWith(filterStr);
    },
    ends_with: (row, columnId, filterValue: FilterValue, _addMeta) => {
      const columnValue = String(row.getValue(columnId)).toLowerCase();
      const filterStr = String(filterValue.value).toLowerCase();
      return columnValue.endsWith(filterStr);
    }
  },
  date: {
    eq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return dayjs(row.getValue(columnId)).isSame(
        dayjs(filterValue.date),
        'day'
      );
    },
    neq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return !dayjs(row.getValue(columnId)).isSame(
        dayjs(filterValue.date),
        'day'
      );
    },
    lt: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return dayjs(row.getValue(columnId)).isBefore(
        dayjs(filterValue.date),
        'day'
      );
    },
    lte: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return dayjs(row.getValue(columnId)).isSameOrBefore(
        dayjs(filterValue.date),
        'day'
      );
    },
    gt: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return dayjs(row.getValue(columnId)).isAfter(
        dayjs(filterValue.date),
        'day'
      );
    },
    gte: (row, columnId, filterValue: FilterValue, _addMeta) => {
      return dayjs(row.getValue(columnId)).isSameOrAfter(
        dayjs(filterValue.date),
        'day'
      );
    }
  },
  select: {
    eq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      if (String(filterValue.value) === EmptyFilterValue) {
        return row.getValue(columnId) === '';
      }
      // Select only supports string values
      return String(row.getValue(columnId)) === String(filterValue.value);
    },
    neq: (row, columnId, filterValue: FilterValue, _addMeta) => {
      if (String(filterValue.value) === EmptyFilterValue) {
        return row.getValue(columnId) !== '';
      }
      // Select only supports string values
      return String(row.getValue(columnId)) !== String(filterValue.value);
    }
  },
  multiselect: {
    in: (row, columnId, filterValue: FilterValue, _addMeta) => {
      if (!Array.isArray(filterValue.value)) return false;

      return filterValue.value
        .map(value => (value === EmptyFilterValue ? '' : String(value)))
        .includes(String(row.getValue(columnId)));
    },
    notin: (row, columnId, filterValue: FilterValue, _addMeta) => {
      if (!Array.isArray(filterValue.value)) return false;

      return !filterValue.value
        .map(value => (value === EmptyFilterValue ? '' : String(value)))
        .includes(String(row.getValue(columnId)));
    }
  }
} as const;

export function getFilterFn<T extends keyof FilterFunctionsMap>(
  type: T,
  operator: FilterOperatorTypes
) {
  // @ts-expect-error FilterOperatorTypes is union of all possible operators
  return filterOperationsMap[type][operator];
}

const handleStringBasedTypes = (
  filterType: FilterTypes,
  value: FilterPrimitive,
  operator?: FilterOperatorTypes | DataTableFilterOperatorTypes
): DataTableFilterValues => {
  switch (filterType) {
    case FilterType.date: {
      const dateValue = dayjs(value as string | Date);
      let stringValue = '';
      if (dateValue.isValid()) {
        try {
          stringValue = dateValue.toISOString();
        } catch {
          stringValue = '';
        }
      }
      return {
        value,
        stringValue
      };
    }
    case FilterType.select:
      return {
        stringValue: value === EmptyFilterValue ? '' : (value as string),
        value
      };
    case FilterType.multiselect:
      return {
        value,
        stringValue: (value as string[])
          .map(item => (item === EmptyFilterValue ? '' : String(item)))
          .join()
      };
    case FilterType.string: {
      const stringVal = value as string;
      let processedValue = stringVal;
      // Apply wildcards for ilike operations
      // (operator could be UI type or already converted to 'ilike')
      if (operator === 'contains') {
        processedValue = `%${stringVal}%`;
      } else if (operator === 'starts_with') {
        processedValue = `${stringVal}%`;
      } else if (operator === 'ends_with') {
        processedValue = `%${stringVal}`;
      } else if (operator === 'ilike') {
        // If already converted to ilike, assume it needs contains-style wildcards
        // unless the value already has wildcards
        if (!stringVal.includes('%')) {
          processedValue = `%${stringVal}%`;
        }
      }
      return {
        stringValue: processedValue,
        value
      };
    }
    default:
      return {
        stringValue: value as string,
        value
      };
  }
};

export const getFilterOperator = ({
  value,
  filterType,
  operator
}: {
  value: FilterPrimitive;
  filterType?: FilterTypes;
  operator: FilterOperatorTypes;
}): DataTableFilterOperatorTypes => {
  if (value === EmptyFilterValue && filterType === FilterType.select) {
    return 'empty';
  }

  // Map string filter operators to ilike for DataTableFilter
  if (
    filterType === FilterType.string &&
    (operator === 'contains' ||
      operator === 'starts_with' ||
      operator === 'ends_with')
  ) {
    return 'ilike';
  }

  return operator as DataTableFilterOperatorTypes;
};

export const getFilterValue = ({
  value,
  dataType = 'string',
  filterType = FilterType.string,
  operator
}: {
  value: FilterPrimitive;
  dataType?: FilterValueType;
  filterType?: FilterTypes;
  operator?: FilterOperatorTypes | DataTableFilterOperatorTypes;
}): DataTableFilterValues => {
  if (dataType === 'boolean') {
    return { boolValue: value as boolean, value };
  }
  if (dataType === 'number') {
    return { numberValue: value as number, value };
  }

  // Handle string-based types
  return handleStringBasedTypes(filterType, value, operator);
};

export const getDataType = ({
  filterType = FilterType.string,
  dataType = 'string'
}: {
  dataType?: FilterValueType;
  filterType?: FilterTypes;
}): FilterValueType => {
  switch (filterType) {
    case FilterType.multiselect:
    case FilterType.select:
      return dataType;
    case FilterType.date:
      return 'string';
    default:
      return filterType;
  }
};
